import { useFormBehaviors } from 'hooks';
import { createContext, FC, useContext, useMemo } from 'react';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useGeneralSettingsDataContext } from './GeneralSettingsPage.data.context';
import { useGQLMutation } from 'hooks/useGQLMutation.ts';
import {
  Mutation_UpdateOrganizationTimeFormatMutation,
  Mutation_UpdateOrganizationTimeFormatMutationVariables,
  NotificationPreferenceControlEnum,
  TimeFormatEnum,
} from 'types/graphql';
import { MUTATION_updateOrganizationTimeFormat } from 'mutation';
import { toastService } from 'services';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { useTranslation } from 'hooks/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from 'store/selectors/userSelectors/userSelector';
import { authSelector } from 'store/selectors/userSelectors/userSelector';
import isUserEssentialSelector from 'store/selectors/userSelectors/isUserEssentialSelector';
import { defaultApolloClient } from 'providers/ApolloProvider';
import { fetchMeData } from 'store/reducers/auth/authReducer.constants';
import { SessionService } from 'services';
import { useHistory } from 'react-router-dom';
import { HARDCODED_SHOW_SMS_EMAIL_NOTIFICATIONS_ON_GENERAL_SETTINGS_PAGE } from 'constants/hardcoded';

interface GeneralSettingsForm {
  is24HourTimeFormat: boolean;
  licenceNumber: string;
  smsNotificationsEnabled: boolean;
  emailShiftNotificationsEnabled: boolean;
  notificationPreferenceControl: NotificationPreferenceControlEnum;
  smsPricingConfirmed: boolean;
}

export const GeneralSettingsFormContext = createContext(
  {} as UseFormBehaviors<GeneralSettingsForm>,
);

export const GeneralSettingsFormProvider: FC = ({ children }) => {
  const { data, fetch } = useGeneralSettingsDataContext();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const auth = useSelector(authSelector);
  const isUserEssential = useSelector(isUserEssentialSelector);
  const history = useHistory();

  const { mutate, ...rest } = useGQLMutation<
    Mutation_UpdateOrganizationTimeFormatMutation,
    Mutation_UpdateOrganizationTimeFormatMutationVariables
  >({
    key: 'UPDATE_ORGANIZATION_TIME_FORMAT',
    mutation: MUTATION_updateOrganizationTimeFormat,
    refetchQueries: ['organizationSettingsPageGeneralSettings', 'USER_PROFILE_PAGE'],
    onSuccess: async () => {
      if (auth?.baseUrl && user) {
        try {
          const updatedUserData = await fetchMeData({ baseUrl: auth.baseUrl });
          const newTimeFormat = (updatedUserData.time_format as TimeFormatEnum) || user.time_format;

          dispatch({
            type: 'auth/updateUserTimeFormat',
            payload: {
              time_format: newTimeFormat,
            },
          });

          SessionService.updateTimeFormat(newTimeFormat);
        } catch (error) {
          console.error('Failed to refetch user data after time format update:', error);
        }
      }

      await defaultApolloClient.refetchQueries({ include: ['USER_PROFILE_PAGE'] });

      toastService.success(t('settings:Successfully_updated'));
      fetch();
      history.push('/settings/organization/general-settings');
    },
    onError: handleGenericErrorMessage,
  });
  const is24HourTimeFormat = useMemo(() => {
    const orgTimeFormat = data?.fetchCommunity?.organization?.timeFormatConfiguration;
    if (orgTimeFormat !== undefined) {
      return orgTimeFormat === TimeFormatEnum.military;
    }
    return false;
  }, [data?.fetchCommunity?.organization?.timeFormatConfiguration]);

  const form = useFormBehaviors({
    initialState: {
      is24HourTimeFormat: is24HourTimeFormat,
      licenceNumber: data?.fetchCommunity?.organization?.licenceNumber || '',
      smsNotificationsEnabled: data?.fetchCommunity?.organization?.smsNotificationsEnabled ?? false,
      emailShiftNotificationsEnabled:
        data?.fetchCommunity?.organization?.emailShiftNotificationsEnabled ?? false,
      notificationPreferenceControl:
        data?.fetchCommunity?.organization?.notificationPreferenceControl ??
        NotificationPreferenceControlEnum.SCHEDULER_ONLY,
      smsPricingConfirmed: false,
    },
    ...rest,
    onSubmit: (values) => {
      const organizationId = data?.fetchCommunity?.organization?.relayId;
      if (!organizationId) {
        toastService.error(t('settings:Organization_not_found'));
        return Promise.reject(new Error('Organization ID is required'));
      }

      const rawNotificationPreferenceControl = values.notificationPreferenceControl as
        | NotificationPreferenceControlEnum
        | NotificationPreferenceControlEnum[];
      const notificationPreferenceControl = Array.isArray(rawNotificationPreferenceControl)
        ? rawNotificationPreferenceControl[0]
        : rawNotificationPreferenceControl;

      const isSMSPricingAlreadyConfirmed =
        !!data?.fetchCommunity?.organization?.smsPricingConfirmedAt;

      return mutate({
        variables: {
          organizationId: String(organizationId),
          organizationAttributes: {
            timeFormat: values.is24HourTimeFormat
              ? TimeFormatEnum.military
              : TimeFormatEnum.standard,
            licenceNumber: values.licenceNumber,
            ...(!isUserEssential && HARDCODED_SHOW_SMS_EMAIL_NOTIFICATIONS_ON_GENERAL_SETTINGS_PAGE
              ? {
                  smsNotificationsEnabled: values.smsNotificationsEnabled,
                  emailShiftNotificationsEnabled: values.emailShiftNotificationsEnabled,
                  notificationPreferenceControl,
                  ...(values.smsNotificationsEnabled && !isSMSPricingAlreadyConfirmed
                    ? { smsPricingConfirmed: true }
                    : {}),
                }
              : {}),
          },
        },
      });
    },
  });

  const value = useMemo(() => form, [form]);

  return (
    <GeneralSettingsFormContext.Provider value={value}>
      {children}
    </GeneralSettingsFormContext.Provider>
  );
};

export const useGeneralSettingsFormContext = () => useContext(GeneralSettingsFormContext);
